<template>
  <teleport to="body">
    <dialog
      ref="dialog"
      v-on-click-outside="onClickOutside"
      closedby="none"
      aria-modal="true"
      v-bind="$attrs"
      :class="modal.open.value ? 'fixed inset-0 m-0 flex h-dvh w-screen flex-col overscroll-y-auto' : ''"
      :style="{ backgroundColor: backdropVariant({ weight: backdrop }) }"
    >
      <div
        v-if="modal.open.value"
        :class="!fullscreen ? 'absolute left-1/2 -translate-x-1/2' : 'w-full flex h-full'"
        :style="!fullscreen ? modalSizing : undefined"
      >
        <div
          ref="modalContent"
          class="shadow-md"
          :class="[
            width,
            layout,
            borderRadius,
            modalVariant({ backgroundColour: background, layout }),
            fullscreen ? 'relative overflow-y-auto !rounded-none' : 'overflow-y-hidden',
          ]"
        >
          <slot name="header">
            <gv-modal-header :modal="modal" :title="title" />
          </slot>
          <slot />
          <slot name="footer" />
        </div>
      </div>
    </dialog>
  </teleport>
</template>

<script lang="ts" setup>
import { ref, PropType, computed, watch, onBeforeUnmount, nextTick, useTemplateRef, onMounted } from 'vue';
import { vOnClickOutside } from '@vueuse/components';
import { UseModal } from '@/common/composables/useModal';
import { usePoller } from '@/common/composables';
import { Poller } from '@/features';
import { useElementSize } from '@vueuse/core';
import { tw } from '@/common';
import { cva, VariantProps } from 'class-variance-authority';

const emit = defineEmits(['open', 'close']);

const props = defineProps({
  modal: {
    type: Object as PropType<UseModal>,
    required: true,
  },
  minHeight: { type: Number, required: true },
  title: String,
  width: String,
  fullscreen: { type: Boolean, default: false },
  closeOnClickOutside: { type: Boolean, default: true },
  layout: { type: String as PropType<VariantProps<typeof modalVariant>['layout']>, default: 'default' },
  borderRadius: { type: String, default: 'rounded-lg' },
  backdrop: { type: String as PropType<VariantProps<typeof backdropVariant>['weight']>, default: 'light' },
  background: { type: String as PropType<VariantProps<typeof modalVariant>['backgroundColour']>, default: 'white' },
});

const poller = usePoller();

const modalVariant = cva([tw`flex`], {
  variants: {
    layout: {
      'flex-row': tw`flex-row`,
      'flex-col': tw`flex-col`,
    },
    backgroundColour: {
      white: tw`bg-white`,
      transparent: tw`bg-transparent`,
    },
  },
});

// eslint-disable-next-line tailwindcss/no-custom-classname
const backdropVariant = cva('', {
  variants: {
    weight: {
      light: 'rgba(0,0,0,0.25)',
      heavy: 'rgba(16,19,25,0.75)',
    },
  },
});

const dialog = useTemplateRef('dialog');
const modalContent = useTemplateRef('modalContent');

const needsExtraPadding = ref(false);

const modalSizing = computed(() => ({
  minHeight: `${props.minHeight}px`,
  paddingTop: `calc(50vh - ${props.minHeight / 2}px)`,
  paddingBottom: needsExtraPadding.value ? `calc(50vh - ${props.minHeight / 2}px)` : '1rem',
}));

const checkSpacing = async () => {
  if (!dialog.value) return;

  needsExtraPadding.value = false;
  await nextTick();

  const scrollContainerScrollHeight = dialog.value.scrollHeight;
  const scrollContainerClientHeight = dialog.value.clientHeight;

  needsExtraPadding.value = scrollContainerScrollHeight > scrollContainerClientHeight;
};

watch(
  () => props.modal.open.value,
  async (open) => {
    emit(open ? 'open' : 'close');

    if (open) {
      dialog.value?.showModal();

      window.addEventListener('resize', checkSpacing);
    } else {
      window.removeEventListener('resize', checkSpacing);
      dialog.value?.close();
    }

    poller.changeInterval(Poller.fetchAllHazards, props.modal.open.value ? 3 * 60 * 1000 : 60 * 1000);
    poller.startPoller(Poller.fetchAllHazards);
  }
);

watch(
  () => props.modal.open.value,
  async (open) => {
    if (open) {
      await nextTick();

      //! Hack to ensure the modal is rendered before checking spacing. Otherwise the scroll height found is before any padding is applied.
      setTimeout(() => {
        checkSpacing();
      }, 50);
    }
  },
  { immediate: true }
);

const modalSize = useElementSize(modalContent);
watch(
  modalSize.height,
  () => {
    if (props.fullscreen) return;

    checkSpacing();
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkSpacing);
});

const onClickOutside = () => {
  if (!props.closeOnClickOutside) return;
  props.modal.setClosed();
};

onMounted(() => {
  if (props.modal.open.value) {
    dialog.value?.showModal();
  }
});
</script>
<style scoped>
dialog:modal {
  max-width: 100vw;
  max-height: 100dvh;
}

dialog {
  opacity: 0;
  transform: scale(0.95);
  transition:
    opacity 150ms ease,
    transform 150ms ease;
}

dialog[open] {
  opacity: 1;
  transform: scale(1);
}

dialog:focus {
  outline: none;
}

dialog::backdrop {
  opacity: 0;
  transition: opacity 150ms ease;
}

dialog[open]::backdrop {
  opacity: 1;
}
</style>
